import { motion } from 'framer-motion'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'usehooks-ts'

import { useCartInfoStore } from '@entities/Cart'

import { GoToCheckout } from '@features/go-to-checkout'
import { useDelivery } from '@features/shipping-methods'

import { useZonesFromAddress } from '@shared/hooks/useZonesFromAddress'
import Button from '@shared/ui/button'
import { formatPrice } from '@shared/uttils'
import { formatGramsToHumanWeight } from '@shared/uttils/formatGramsToHumanWeight'

import styles from './CartBottomBar.module.scss'

interface CartBottomBarProps {
  total: number;
}

export default function CartBottomBar (props: CartBottomBarProps) {
  const { total } = props
  const isMobile = useMediaQuery('(max-width: 1179px)')
  const { t: tc } = useTranslation('cart')
  const { t: to } = useTranslation('order')

  const { minOrderCost, isPickup, hasAvailableSlots } = useZonesFromAddress()
  const cartInfo = useCartInfoStore((state) => state.cartInfo)
  const { deliveryType, maxExpressWeight, maxSlotWeight } = useDelivery()

  const cartWeight = useMemo(() => Number(cartInfo?.totalWeight ?? 0), [ cartInfo?.totalWeight ])
  const weightLimit = useMemo(() => {
    return deliveryType === 'express' ? maxExpressWeight : maxSlotWeight
  }, [ deliveryType, maxExpressWeight, maxSlotWeight ])
  const weightExceeded = useMemo(() => {
    if (!weightLimit || weightLimit <= 0) return false
    return cartWeight > weightLimit
  }, [ cartWeight, weightLimit ])

  if (!isMobile) return null

  return (
    <motion.div
      className={styles.root}
      initial={{ y: '100%', opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: '100%', opacity: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      <div className={styles.totalBox}>
        <span className={styles.label}>{tc('total.total')}:</span>
        <span className={styles.value}>{formatPrice(total)}</span>
      </div>
      {weightExceeded ? (
        <Button
          className={styles.button}
          disabled
          size="large"
        >
          {to('weight_exceeded', {
            maxWeight: formatGramsToHumanWeight({
              weight: weightLimit || 0,
              t: tc,
            }),
          })}
        </Button>
      ) : !hasAvailableSlots ? (
        <Button
          className={styles.button}
          disabled
          size="large"
        >
          {tc('order.no_delivery_available')}
        </Button>
      ) : !isPickup() && total < minOrderCost ? (
        <Button
          className={styles.button}
          disabled={!isPickup() && total < minOrderCost}
          size="large"
        >
          {tc('preview.min_order_message', {
            minOrderCost: formatPrice(minOrderCost),
            amount: formatPrice(minOrderCost - total),
          })}
        </Button>
      ) : (
        <GoToCheckout />
      )}
    </motion.div>
  )
}
